import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, X } from "lucide-react";

const STEPS = [
    'Navigate to "Inmates" to add inmates individually or bulk upload a CSV',
    "Assign inmates to units in the inmate records",
    'Configure crisis notification contacts for each unit under "Crisis Alerts"',
    'Generate access codes for inmates in the "Access Codes" section',
    "Share codes with inmates so they can create accounts and access the app",
    'Monitor inmate progress in the "Metrics" section',
    "Review active crisis alerts and respond appropriately",
    'Track daily suicide and violence risk scores in the "Risk Scores" tab',
];

export function InstitutionGuideModal({ open, onClose }) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="institution-guide-overlay"
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        data-testid="institution-guide-modal"
                        className="w-full max-w-lg bg-white rounded-2xl shadow-phoenix-deep overflow-hidden"
                        initial={{ scale: 0.95, y: 12, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.95, y: 12, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 26 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-phoenix-navy px-6 py-5 flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                                <BookOpen className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1">
                                <h2 className="font-display font-bold text-xl text-white leading-tight">Getting Started</h2>
                                <p className="text-sm text-white/70 mt-0.5">Follow these steps to set up your facility</p>
                            </div>
                            <button
                                data-testid="institution-guide-close"
                                onClick={onClose}
                                aria-label="Close guide"
                                className="text-white/70 hover:text-white transition-colors p-1 -mr-1"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <ol className="px-6 py-5 space-y-3 max-h-[55vh] overflow-y-auto">
                            {STEPS.map((step, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-phoenix-orange/15 text-phoenix-orange text-xs font-bold flex items-center justify-center">
                                        {i + 1}
                                    </span>
                                    <span className="text-sm text-foreground leading-relaxed">{step}</span>
                                </li>
                            ))}
                        </ol>

                        <div className="px-6 pb-6 pt-1">
                            <button
                                data-testid="institution-guide-got-it"
                                onClick={onClose}
                                className="w-full py-3 bg-phoenix-navy text-white rounded-xl font-semibold hover:bg-phoenix-navy-2 transition-colors"
                            >
                                Got it
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
